import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PasswordStrengthIndicator } from '@/components/ui/PasswordStrengthIndicator'
import { register } from '@/lib/api/auth'
import { useAuth } from '@/lib/auth-context'
import { User, Mail, Lock, Eye, EyeOff, Building } from 'lucide-react'

const registerSchema = z.object({
  email: z.string().email('Adresse email invalide'),
  password: z.string().min(4, 'Le mot de passe doit contenir au moins 4 caractères'),
  confirmPassword: z.string(),
  firstName: z.string().min(1, 'Le prénom est requis'),
  lastName: z.string().min(1, 'Le nom est requis'),
  organizationName: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirmPassword'],
})

type RegisterFormData = z.infer<typeof registerSchema>

export default function RegisterPage() {
  const navigate = useNavigate()
  const { login: authLogin } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register: registerField,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      organizationName: '',
    },
  })

  const password = watch('password')
  const firstName = watch('firstName')

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await register(
        data.email,
        data.password,
        data.firstName,
        data.lastName,
        data.organizationName || undefined
      )
      authLogin(response)
      navigate('/home')
    } catch (err: unknown) {
      const errData = (err instanceof Error && 'response' in err)
        ? (err as unknown as { response?: { data?: { ErrorCode?: string; Message?: string } } }).response?.data
        : undefined
      const errorCode = errData?.ErrorCode
      const errorMessage = errData?.Message

      if (errorCode === 'EmailAlreadyExists') {
        setError('Cette adresse email est déjà enregistrée.')
      } else if (errorMessage) {
        setError(errorMessage)
      } else {
        setError('Une erreur est survenue lors de l\'inscription.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <h1 className="text-6xl font-bold text-primary mb-4">SOLY</h1>
          <div className="w-32 h-1.5 bg-primary mx-auto rounded-full mb-6"></div>
          <p className="text-lg text-muted-foreground font-medium">
            Créez votre compte
          </p>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {/* Error message */}
          {error && (
            <div data-testid="register-error" className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <span className="text-red-600 text-lg">⚠️</span>
              <p className="text-sm text-red-600 flex-1">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* First name */}
            <div className="space-y-2">
              <label htmlFor="firstName" className="text-sm font-medium">
                Prénom
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Votre prénom"
                  autoComplete="given-name"
                  {...registerField('firstName')}
                  autoFocus
                  data-testid="register-firstname-input"
                  className={`pl-12 h-12 ${
                    errors.firstName ? 'border-red-500 animate-shake' : ''
                  }`}
                />
              </div>
              {errors.firstName && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <span>⚠️</span>
                  <span>{errors.firstName.message}</span>
                </p>
              )}
            </div>

            {/* Last name */}
            <div className="space-y-2">
              <label htmlFor="lastName" className="text-sm font-medium">
                Nom
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Votre nom"
                  autoComplete="family-name"
                  {...registerField('lastName')}
                  data-testid="register-lastname-input"
                  className={`pl-12 h-12 ${
                    errors.lastName ? 'border-red-500 animate-shake' : ''
                  }`}
                />
              </div>
              {errors.lastName && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <span>⚠️</span>
                  <span>{errors.lastName.message}</span>
                </p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Votre adresse email"
                  autoComplete="email"
                  {...registerField('email')}
                  data-testid="register-email-input"
                  className={`pl-12 h-12 ${
                    errors.email ? 'border-red-500 animate-shake' : ''
                  }`}
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <span>⚠️</span>
                  <span>{errors.email.message}</span>
                </p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Choisissez un mot de passe"
                  autoComplete="new-password"
                  {...registerField('password')}
                  data-testid="register-password-input"
                  className={`pl-12 pr-12 h-12 ${
                    errors.password ? 'border-red-500 animate-shake' : ''
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <span>⚠️</span>
                  <span>{errors.password.message}</span>
                </p>
              )}
              <PasswordStrengthIndicator password={password} />
            </div>

            {/* Confirm password */}
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirmez votre mot de passe"
                  autoComplete="new-password"
                  {...registerField('confirmPassword')}
                  data-testid="register-confirm-password-input"
                  className={`pl-12 pr-12 h-12 ${
                    errors.confirmPassword ? 'border-red-500 animate-shake' : ''
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showConfirmPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <span>⚠️</span>
                  <span>{errors.confirmPassword.message}</span>
                </p>
              )}
            </div>

            {/* Organization name (optional) */}
            <div className="space-y-2">
              <label htmlFor="organizationName" className="text-sm font-medium">
                Nom de l'entreprise <span className="text-muted-foreground">(optionnel)</span>
              </label>
              <div className="relative">
                <Building className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="organizationName"
                  type="text"
                  placeholder={firstName ? `${firstName}'s Organization` : "Nom de votre entreprise"}
                  autoComplete="organization"
                  {...registerField('organizationName')}
                  data-testid="register-org-input"
                  className="pl-12 h-12"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Si vide, sera nommé "{firstName ? `${firstName}'s Organization` : "Votre prénom's Organization"}"
              </p>
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              data-testid="register-submit-btn"
              className="w-full h-12 text-base font-semibold mt-6"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="inline-block animate-spin mr-2">⏳</span>
                  Création du compte...
                </>
              ) : (
                'Créer mon compte'
              )}
            </Button>
          </form>

          {/* Back to login */}
          <div className="mt-6 text-center">
            <span className="text-sm text-muted-foreground">Vous avez déjà un compte ? </span>
            <Link
              to="/login"
              className="text-sm text-primary hover:underline font-medium"
            >
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
